/**
 * The flat agenda list of screens 02 and 03 (salon-mockup.html agRow /
 * agFree): visits, break rows (time off, D-41) and free gaps with their length
 * already computed by the server (D-10), in time order.
 */
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { serviceName } from '../lib/serviceName';
import { hm } from '../lib/time';
import type { Appointment, DayView, Gap } from '../types';
import { Icon } from './icons';

export type AgendaItem =
  | { kind: 'visit'; at: string; appt: Appointment }
  | { kind: 'break'; at: string; id: string; reason: string | null; ends: string }
  | { kind: 'gap'; at: string; gap: Gap };

/** Cancelled visits hold no time and are left off the list; the day screen's
 * timeline still draws them (legend «Отменён»). */
export function agendaItems(day: DayView): AgendaItem[] {
  const items: AgendaItem[] = [
    ...day.appointments.filter((a) => a.status !== 'cancelled').map((appt) => ({ kind: 'visit' as const, at: appt.starts_at, appt })),
    ...day.time_off.map((t) => ({ kind: 'break' as const, at: t.starts_at, id: t.id, reason: t.reason, ends: t.ends_at })),
    ...day.free_gaps.map((gap) => ({ kind: 'gap' as const, at: gap.starts_at, gap })),
  ];
  return items.sort((a, b) => a.at.localeCompare(b.at) || order(a) - order(b));
}

const order = (i: AgendaItem) => (i.kind === 'break' ? 0 : i.kind === 'visit' ? 1 : 2);

export function useDuration() {
  const { t } = useTranslation();
  return (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return [h ? t('dur.h', { count: h }) : '', m ? t('dur.m', { count: m }) : ''].filter(Boolean).join(' ');
  };
}

export const VisitRow = forwardRef<
  HTMLDivElement,
  { appt: Appointment; past?: boolean; live?: boolean; minutesLeft?: number; onOpen: () => void }
>(function VisitRow({ appt, past, live, minutesLeft, onOpen }, ref) {
  const { t, i18n } = useTranslation();
  const service = serviceName(appt.service_name_ru, appt.service_name_ky, i18n.language);
  return (
    <div
      ref={ref}
      className={`ag-it${past ? ' past' : ''}${live ? ' live' : ''}`}
      data-id={appt.id}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
    >
      <div className="ag-tm tnum">{hm(appt.starts_at)}</div>
      <div className="ag-bd">
        <div className="ag-nm">
          <span>{appt.client_name}</span>
          {live && minutesLeft !== undefined && <span className="ag-rem">{t('today.left', { count: minutesLeft })}</span>}
          {past && <Icon.tickRow />}
        </div>
        <div className="ag-sv">{service}</div>
      </div>
    </div>
  );
});

export function BreakRow({ reason, starts, ends }: { reason: string | null; starts: string; ends: string }) {
  return (
    <div className="ag-brk">
      <span>{reason}</span>
      <span className="tnum">
        {hm(starts)} — {hm(ends)}
      </span>
    </div>
  );
}

export function GapRow({ gap, onBook, locked }: { gap: Gap; onBook: () => void; locked?: boolean }) {
  const { t } = useTranslation();
  const duration = useDuration();
  return (
    <div
      className="ag-gap"
      role={locked ? undefined : 'button'}
      tabIndex={locked ? undefined : 0}
      // screen 20: on an expired trial a gap is no longer a button
      style={locked ? { opacity: 0.5, cursor: 'default', borderStyle: 'solid' } : undefined}
      onClick={locked ? undefined : onBook}
      onKeyDown={(e) => !locked && e.key === 'Enter' && onBook()}
    >
      <div>
        <div className="ag-g1">{t('dur.free', { span: duration(gap.minutes) })}</div>
        <div className="ag-g2 tnum">
          {hm(gap.starts_at)} — {hm(gap.ends_at)}
        </div>
      </div>
      {!locked && (
        <span className="ag-ar">
          <Icon.chev />
        </span>
      )}
    </div>
  );
}
